import { create } from 'zustand';
import { devtools, persist, subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { apiFetch, AuthError, authFetch, setTokenProvider } from '@/lib/http';
import { hardLogout, type LogoutReason } from '@/lib/auth/hardLogout';
import { isDemoGuestAccessToken } from '@/lib/auth/demoGuest';

const TOKEN_REFRESH_BUFFER_MS = 60_000; // Refresh 1 minute before expiry

interface AuthUser {
  id: string;
  email: string;
  displayName: string;
  role: string;
}

export interface TokenPair {
  access_token: string;
  refresh_token: string;
  expires_in: number;
}

export interface AuthResult {
  tokens: TokenPair;
  user: {
    id: string;
    email: string;
    display_name: string;
  };
}

type AuthStatus = 'idle' | 'loading' | 'authenticated' | 'unauthenticated';

/**
 * Signup-time consent attestation. The client sends only "I checked the box"
 * booleans — the server stamps the authoritative terms/privacy version constants
 * (R28-P3). A forged or stale client cannot record a bogus accepted-version.
 */
interface ConsentAttestation {
  ageConfirmed: boolean;
  acceptedTerms: boolean;
  acceptedPrivacy: boolean;
}

interface AuthStoreState {
  status: AuthStatus;
  accessToken: string | null;
  refreshToken: string | null;
  expiresAt: number | null;
  user: AuthUser | null;
  error: string | null;
  /**
   * True when the current access token carries the `/demo` guest scope.
   *
   * DERIVED, never set by an action: a single module-level subscription below
   * recomputes it from `accessToken`, so login, refresh, cross-tab adoption,
   * persist-rehydration and logout can't leave it stale. Excluded from
   * `partialize` — it is recomputed from the persisted token on boot rather
   * than trusted from storage.
   *
   * PRESENTATION ONLY. See `@/lib/auth/demoGuest` — read-only is enforced by
   * the server, and this flag must never gate a request the server would
   * otherwise have answered.
   */
  isDemoGuest: boolean;

  actions: {
    login: (email: string, password: string) => Promise<boolean>;
    register: (
      email: string,
      password: string,
      displayName: string,
      consent: ConsentAttestation,
      inviteToken?: string,
      /**
       * First-touch referral code (`?ref=`), persisted by
       * features/referral/refCapture and sent as `ref`. Omitted entirely when
       * absent — an empty string would be a code the server has to reject.
       */
      ref?: string,
    ) => Promise<boolean>;
    requestMagicLink: (email: string) => Promise<boolean>;
    validateMagicLink: (token: string) => Promise<boolean>;
    adoptAuthResult: (result: AuthResult) => void;
    /**
     * Swap in a re-minted token pair for the CURRENT session (AUTH-04 password
     * change). Unlike adoptAuthResult it carries no user object — the identity
     * is unchanged; only the tokens' password-epoch claim moved. Every other
     * session's tokens are dead the moment the change commits, so failing to
     * adopt here would strand this tab on a token that dies at its next
     * validation.
     */
    adoptTokenPair: (pair: TokenPair) => void;
    /**
     * Install a `/demo` guest session from `POST /api/demo/session`.
     *
     * Deliberately NOT adoptAuthResult: the demo endpoint returns an access
     * token and nothing else — no refresh token (a guest session ends when the
     * token expires; the server never persists a guest session id, so a refresh
     * could not succeed) and no user object (the token carries no email). So no
     * refresh is scheduled, and the display identity is synthesized locally
     * rather than invented server-side.
     */
    adoptDemoSession: (accessToken: string, expiresIn: number, displayName: string) => void;
    refresh: () => Promise<boolean>;
    /** Return a non-expired access token, refreshing first if the current one is missing/stale. */
    ensureFreshAccessToken: () => Promise<string | null>;
    /** On a dropped connection: try to refresh+reconnect; only hard-logout on a genuine auth rejection. */
    recoverOrLogout: (reconnect: () => void) => Promise<void>;
    logout: () => Promise<void>;
    /** `redirectTo` overrides the post-wipe landing route (see hardLogout). */
    forceLogout: (reason: LogoutReason, redirectTo?: string) => Promise<void>;
    setRole: (role: string) => void;
    initialize: () => Promise<void>;
  };
}

let refreshTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleRefresh(expiresIn: number, refreshFn: () => Promise<boolean>) {
  if (refreshTimer) clearTimeout(refreshTimer);
  const delay = Math.max(expiresIn * 1000 - TOKEN_REFRESH_BUFFER_MS, 5000);
  refreshTimer = setTimeout(() => {
    void refreshFn();
  }, delay);
}

function clearRefreshTimer() {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

function handleAuthResult(
  set: (fn: (state: AuthStoreState) => void) => void,
  get: () => AuthStoreState,
  result: AuthResult,
) {
  const expiresAt = Date.now() + result.tokens.expires_in * 1000;
  set((state) => {
    state.accessToken = result.tokens.access_token;
    state.refreshToken = result.tokens.refresh_token;
    state.expiresAt = expiresAt;
    state.user = {
      id: result.user.id,
      email: result.user.email,
      displayName: result.user.display_name || result.user.email,
      role: '',
    };
    state.status = 'authenticated';
    state.error = null;
  });
  scheduleRefresh(result.tokens.expires_in, get().actions.refresh);
}

// ── Refresh coordination ────────────────────────────────────────────────────
// The backend rotates refresh tokens single-use (replay → ErrSessionRevoked). To
// avoid redeeming a token twice we coordinate at three levels:
//   1. single-flight   — dedupe concurrent refresh() calls within this context
//   2. Web Locks        — serialize refresh across tabs / Zen workspaces
//   3. adopt-or-skip    — after acquiring the lock, re-read the freshest persisted
//                         token: if another context already refreshed, adopt it and
//                         skip the network call; otherwise redeem the *rotated* token.
// See docs/plans/2026-06-25-client-at-rest-key-protection-design.md §10 (RC-B).
const AUTH_PERSIST_KEY = 'lvdr_auth';
let refreshInFlight: Promise<boolean> | null = null;

interface PersistedAuth {
  accessToken: string | null;
  refreshToken: string | null;
  expiresAt: number | null;
  user: AuthUser | null;
}

function readPersistedAuth(): PersistedAuth | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(AUTH_PERSIST_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { state?: PersistedAuth } & Partial<PersistedAuth>;
    const s = parsed?.state ?? parsed;
    if (!s || typeof s !== 'object') return null;
    return s as PersistedAuth;
  } catch {
    return null;
  }
}

type LockManagerLike = { request: (name: string, fn: () => Promise<unknown>) => Promise<unknown> };

async function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks =
    typeof navigator !== 'undefined'
      ? (navigator as Navigator & { locks?: LockManagerLike }).locks
      : undefined;
  if (locks?.request) {
    return locks.request('lvdr-refresh', fn as () => Promise<unknown>) as Promise<T>;
  }
  return fn();
}

async function doRefresh(
  set: (fn: (state: AuthStoreState) => void) => void,
  get: () => AuthStoreState,
): Promise<boolean> {
  // Another context may have rotated the token while we were queued on the lock.
  const persisted = readPersistedAuth();

  // If someone already refreshed to a comfortably-valid access token, adopt it and skip.
  if (
    persisted?.accessToken &&
    persisted.expiresAt &&
    persisted.expiresAt > Date.now() + TOKEN_REFRESH_BUFFER_MS
  ) {
    set((s) => {
      s.accessToken = persisted.accessToken;
      s.refreshToken = persisted.refreshToken;
      s.expiresAt = persisted.expiresAt;
      if (persisted.user) s.user = persisted.user;
      s.status = 'authenticated';
    });
    scheduleRefresh(Math.max((persisted.expiresAt - Date.now()) / 1000, 5), get().actions.refresh);
    return true;
  }

  // Redeem the freshest refresh token visible — a persisted rotation beats a stale in-memory copy.
  const refreshToken = persisted?.refreshToken ?? get().refreshToken;
  if (!refreshToken) {
    set((s) => {
      s.status = 'unauthenticated';
    });
    return false;
  }

  try {
    const result = await apiFetch<TokenPair>('/api/v2/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    const expiresAt = Date.now() + result.expires_in * 1000;
    set((s) => {
      s.accessToken = result.access_token;
      s.refreshToken = result.refresh_token;
      s.expiresAt = expiresAt;
      s.status = 'authenticated';
    });
    scheduleRefresh(result.expires_in, get().actions.refresh);
    return true;
  } catch (err) {
    clearRefreshTimer();
    const isAuthRejection = err instanceof AuthError && err.status === 401;
    if (isAuthRejection) {
      // Refresh token genuinely rejected (revoked / expired) — clear the session.
      set((s) => {
        s.status = 'unauthenticated';
        s.accessToken = null;
        s.refreshToken = null;
        s.expiresAt = null;
        s.user = null;
      });
    } else {
      // Transient network/server error — preserve credentials so the session survives
      // a blip; a later scheduled refresh or reconnect recovers it (RC-A/aggravator).
      set((s) => {
        if (s.accessToken || s.refreshToken) {
          if (s.status === 'loading' || s.status === 'idle') s.status = 'authenticated';
        } else {
          s.status = 'unauthenticated';
        }
      });
    }
    return false;
  }
}

export const useAuthStore = create<AuthStoreState>()(
  devtools(
    subscribeWithSelector(
      persist(
        immer((set, get) => ({
          status: 'idle' as AuthStatus,
          accessToken: null,
          refreshToken: null,
          expiresAt: null,
          user: null,
          error: null,
          isDemoGuest: false,

          actions: {
            login: async (email, password) => {
              set((s) => { s.status = 'loading'; s.error = null; });
              try {
                const result = await apiFetch<AuthResult>('/api/v2/auth/login', {
                  method: 'POST',
                  body: JSON.stringify({ email, password }),
                });
                handleAuthResult(set, get, result);
                return true;
              } catch (err) {
                // Prefer the machine code (e.g. "sso_required") when the
                // backend sent one — the login page maps codes to friendly
                // copy via loginPage.authErrors.*; err.message is now the
                // LOCALIZED display string (R8 1b), not a matchable token.
                const message =
                  err instanceof AuthError ? (err.code ?? err.message) : 'Unable to connect. Check your connection.';
                set((s) => { s.status = 'unauthenticated'; s.error = message; });
                return false;
              }
            },

            register: async (email, password, displayName, consent, inviteToken?, ref?) => {
              set((s) => { s.status = 'loading'; s.error = null; });
              try {
                const result = await apiFetch<AuthResult>('/api/v2/auth/register', {
                  method: 'POST',
                  body: JSON.stringify({
                    email,
                    password,
                    display_name: displayName,
                    // Booleans only — the server owns the terms/privacy version strings.
                    age_confirmed: consent.ageConfirmed,
                    accepted_terms: consent.acceptedTerms,
                    accepted_privacy: consent.acceptedPrivacy,
                    ...(inviteToken && { invite_token: inviteToken }),
                    // Referral attribution — see the `ref` param's doc on the
                    // action signature.
                    ...(ref && { ref }),
                  }),
                });
                handleAuthResult(set, get, result);
                return true;
              } catch (err) {
                const message = err instanceof AuthError ? err.message : 'Unable to connect. Check your connection.';
                set((s) => { s.status = 'unauthenticated'; s.error = message; });
                return false;
              }
            },

            requestMagicLink: async (email) => {
              set((s) => { s.error = null; });
              try {
                await apiFetch<{ message: string }>('/api/v2/auth/magic-link/request', {
                  method: 'POST',
                  body: JSON.stringify({ email }),
                });
                return true;
              } catch (err) {
                if (err instanceof AuthError && err.status === 404) {
                  // Do not reveal whether the email exists
                  return true;
                }
                const message = err instanceof AuthError
                  ? err.message
                  : 'Unable to connect. Please try again.';
                set((s) => { s.error = message; });
                return false;
              }
            },

            validateMagicLink: async (token) => {
              set((s) => { s.status = 'loading'; s.error = null; });
              try {
                const result = await apiFetch<AuthResult>(
                  `/api/v2/auth/magic-link/validate?token=${encodeURIComponent(token)}`,
                );
                handleAuthResult(set, get, result);
                return true;
              } catch (err) {
                const message = err instanceof AuthError
                  ? (err.status === 410 ? 'This link has expired or was already used.' : err.message)
                  : 'Unable to validate link.';
                set((s) => { s.status = 'unauthenticated'; s.error = message; });
                return false;
              }
            },

            // Adopt an externally-obtained AuthResult (e.g. the SSO ticket
            // exchange on /auth/sso/complete and /auth/sso/link, which call the
            // backend themselves because their endpoints carry extra fields like
            // `needs_bootstrap`). Performs exactly the same store population and
            // refresh scheduling as a successful login().
            adoptAuthResult: (result) => {
              handleAuthResult(set, get, result);
            },

            adoptTokenPair: (pair) => {
              const expiresAt = Date.now() + pair.expires_in * 1000;
              set((s) => {
                s.accessToken = pair.access_token;
                s.refreshToken = pair.refresh_token;
                s.expiresAt = expiresAt;
              });
              scheduleRefresh(pair.expires_in, get().actions.refresh);
            },

            adoptDemoSession: (accessToken, expiresIn, displayName) => {
              clearRefreshTimer();
              set((s) => {
                s.accessToken = accessToken;
                s.refreshToken = null;
                s.expiresAt = Date.now() + expiresIn * 1000;
                s.user = {
                  id: 'demo',
                  // No email is fabricated. The guest token carries none (the
                  // issuer passes "" deliberately), and inventing one would put
                  // a false identity in the account menu — the demo's whole job
                  // is to be honest about what it is.
                  email: '',
                  displayName,
                  role: 'user',
                };
                s.status = 'authenticated';
                s.error = null;
              });
              // No scheduleRefresh: there is no refresh token to redeem. The
              // session simply ends when the token expires.
            },

            refresh: async () => {
              // Single-flight: concurrent callers share one in-flight refresh so a
              // single-use rotating token is never redeemed twice within this context.
              if (refreshInFlight) return refreshInFlight;
              refreshInFlight = withRefreshLock(() => doRefresh(set, get));
              try {
                return await refreshInFlight;
              } finally {
                refreshInFlight = null;
              }
            },

            ensureFreshAccessToken: async () => {
              const { accessToken, expiresAt } = get();
              if (accessToken && expiresAt && expiresAt > Date.now() + TOKEN_REFRESH_BUFFER_MS) {
                return accessToken;
              }
              await get().actions.refresh();
              return get().accessToken;
            },

            recoverOrLogout: async (reconnect: () => void) => {
              const ok = await get().actions.refresh();
              if (ok) {
                reconnect();
                return;
              }
              // refresh() clears tokens only on a genuine auth rejection (401). It has
              // already nulled the session in that case — which makes forceLogout
              // idempotent-skip — so run the credential wipe directly here. A transient
              // network failure leaves tokens intact → retry instead of wiping (the
              // reconnect aggravator we're fixing).
              if (get().status === 'unauthenticated' && !get().refreshToken) {
                clearRefreshTimer();
                await hardLogout('session_revoked');
              } else {
                reconnect();
              }
            },

            logout: async () => {
              const { accessToken } = get();
              clearRefreshTimer();
              if (accessToken) {
                try {
                  await authFetch('/api/v2/auth/logout', accessToken, { method: 'POST' });
                } catch {
                  // Best-effort — clear local state regardless
                }
              }
              set((s) => {
                s.status = 'unauthenticated';
                s.accessToken = null;
                s.refreshToken = null;
                s.expiresAt = null;
                s.user = null;
                s.error = null;
              });
              // Sign out forgets the session exactly as a lock does: one implementation of
              // "forget this session" (hardLogout). Before NOTES-16 this path left every
              // `lvdr_session_*` key — note drafts, workspace note text — on disk.
              await hardLogout('manual');
            },

            forceLogout: async (reason: LogoutReason, redirectTo?: string) => {
              if (get().status === 'unauthenticated') {
                return; // idempotent
              }
              const { accessToken } = get();
              clearRefreshTimer();
              // best-effort server logout (don't await)
              if (accessToken) {
                void Promise.resolve(
                  authFetch('/api/v2/auth/logout', accessToken, { method: 'POST' }),
                ).catch(() => {});
              }
              // clear in-memory state immediately
              set((s) => {
                s.status = 'unauthenticated';
                s.accessToken = null;
                s.refreshToken = null;
                s.expiresAt = null;
                s.user = null;
                s.error = null;
              });
              await hardLogout(reason, redirectTo);
            },

            setRole: (role) => {
              set((s) => {
                if (s.user) s.user.role = role;
              });
            },

            initialize: async () => {
              const { accessToken, refreshToken, status } = get();
              // Already authenticated or loading — skip
              if (status === 'authenticated' || status === 'loading') return;
              // No tokens — unauthenticated
              if (!accessToken && !refreshToken) {
                set((s) => { s.status = 'unauthenticated'; });
                return;
              }
              // Have tokens — try refresh to validate session
              set((s) => { s.status = 'loading'; });
              await get().actions.refresh();
            },
          },
        })),
        {
          name: 'lvdr_auth',
          partialize: (state) => ({
            accessToken: state.accessToken,
            refreshToken: state.refreshToken,
            expiresAt: state.expiresAt,
            user: state.user,
          }),
        },
      ),
    ),
    { name: 'AuthStore' },
  ),
);

// Register the token provider so apiFetch automatically injects Authorization headers.
// The lambda reads live state on every request, so it always uses the current token.
setTokenProvider(() => useAuthStore.getState().accessToken);

// ── Demo-guest flag ─────────────────────────────────────────────────────────
// ONE derivation point for `isDemoGuest`, driven off the token itself rather
// than set by each auth action. handleAuthResult, doRefresh, logout,
// forceLogout and the cross-tab adopt path all end in a write to `accessToken`,
// so subscribing to that field covers every one of them — including ones added
// later, which is why this is a subscription and not five call-site updates.
function syncDemoGuest(token: string | null) {
  const flag = isDemoGuestAccessToken(token);
  if (useAuthStore.getState().isDemoGuest !== flag) {
    useAuthStore.setState((s) => {
      s.isDemoGuest = flag;
    });
  }
}
useAuthStore.subscribe((s) => s.accessToken, syncDemoGuest);
// Persist rehydration is synchronous for the localStorage backend, so the
// restored token is already in state here — seed the flag from it.
syncDemoGuest(useAuthStore.getState().accessToken);

/**
 * useIsDemoGuest — the read hook for presentation code.
 *
 * COSMETIC. Hides account-scoped affordances that are meaningless on the one
 * shared demo identity; it is not, and must not be used as, an access control.
 */
export function useIsDemoGuest(): boolean {
  return useAuthStore((s) => s.isDemoGuest);
}
